import {
  type Schedule,
  ScheduleFrequencyEnum,
} from '@ulysses-inc/harami_api_client'

/**
 * 旧スケジュールの繰り返し周期（N週ごと・Nヵ月ごと・N日ごと）の下限と上限。
 *
 * 週・月は、作業指示（新モデル）が保存できる上限（週 26・月 72）に合わせる（HRMSHI-244）。
 * 旧スケジュールは作業指示へ写すので、写せない大きさの周期を新しく作れないようにする。
 * カスタム（N日ごと）は既存の 180 が作業指示の上限（366）より厳しいのでそのまま。
 * API は週・月の周期を検証しないため、この上限は管理画面だけで効く。
 */
export const SCHEDULE_EVERY_MIN = 1
export const SCHEDULE_EVERY_MAX = {
  weekly: 26,
  monthly: 72,
  custom: 180,
} as const

export type ScheduleEveryOverLimit = {
  /** 周期の単位（「週」「ヵ月」「日」） */
  unit: string
  max: number
}

/**
 * 周期が上限を超えていれば、その周期の単位と上限を返す。
 * 一覧の「複製」は編集フォームの validationSchema を通らずに API へ送るので、同じ上限をここで確かめる。
 */
export const findScheduleEveryOverLimit = (
  schedule: Pick<
    Schedule,
    'frequency' | 'frequencyWeekly' | 'frequencyMonthly' | 'frequencyCustom'
  >,
): ScheduleEveryOverLimit | undefined => {
  switch (schedule.frequency) {
    case ScheduleFrequencyEnum.Weekly:
      return overLimit(
        schedule.frequencyWeekly?.every,
        SCHEDULE_EVERY_MAX.weekly,
        '週',
      )
    case ScheduleFrequencyEnum.Monthly:
      return overLimit(
        schedule.frequencyMonthly?.every,
        SCHEDULE_EVERY_MAX.monthly,
        'ヵ月',
      )
    case ScheduleFrequencyEnum.Custom:
      return overLimit(
        schedule.frequencyCustom?.every,
        SCHEDULE_EVERY_MAX.custom,
        '日',
      )
    default:
      return undefined
  }
}

const overLimit = (
  every: number | undefined,
  max: number,
  unit: string,
): ScheduleEveryOverLimit | undefined =>
  every !== undefined && every > max ? { unit, max } : undefined
